<script setup lang="ts">
import { computed, markRaw, ref, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";
import { RouterLink } from "vue-router";

import AttachmentChips from "~/components/chat/AttachmentChips.vue";
import Button from "~/components/dumb/Button.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import DropdownToggleItem from "~/components/dumb/DropdownToggleItem.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { LEXICAL_OUTSIDE_CLASS } from "~/components/text/const";
import DartLink from "~/components/text/nodes/DartLink.vue";
import PageLink from "~/components/text/plugins/PageLink.vue";
import { ADD_ATTACHMENT_OPTION_VALUE, createChatContextOptionFns } from "~/components/text/plugins/typeahead/links";
import TypeaheadEntityItem from "~/components/text/plugins/typeahead/TypeaheadEntityItem.vue";
import { ENTITY_KIND_URL } from "~/constants/app";
import { ConnectorsIcon, LinkIcon, OpenInternalLinkIcon, PlusIcon, ViewIcon, WebsiteIcon } from "~/icons";
import { makeLinkToSettingsRef } from "~/router/common";
import { isDefined } from "~/shared/common";
import {
  AiModel,
  AiModelWithoutAuto,
  ButtonSize,
  ButtonStyle,
  EntityName,
  ExecutionMode,
  IconSize,
  PageLinkMode,
  Placement,
  TypeaheadSpecialEntity,
  UserRole,
} from "~/shared/enums";
import type {
  Chat,
  ExtraItem,
  MultiselectDropdownMenuItem,
  Skill,
  TypeaheadGetOptionsResult,
  TypeaheadOption,
  ValidationFunctionResult,
} from "~/shared/types";
import { useAppStore, useDataStore, useUserStore } from "~/stores";
import { getItemCountText } from "~/utils/common";
import { getTitleOrNoTitle } from "~/utils/entityTitle";
import usePaginatedList from "~/utils/usePaginatedList";
import { isValidUrlIncludingLocal } from "~/utils/validation";

const TYPEAHEAD_ENTITY_TO_ENTITY_NAME_MAP = new Map([
  [TypeaheadSpecialEntity.TASK, EntityName.TASK],
  [TypeaheadSpecialEntity.DOC, EntityName.DOC],
  [TypeaheadSpecialEntity.DARTBOARD, EntityName.DARTBOARD],
  [TypeaheadSpecialEntity.VIEW, EntityName.VIEW],
  [TypeaheadSpecialEntity.FOLDER, EntityName.FOLDER],
]);

const MAX_VISIBLE_ENTITIES = 5;

const props = defineProps<{
  chat: Chat;
  extras: ExtraItem[];
  isDraft: boolean;
  attachmentDuids: string[];
  isMenu?: boolean;
  hideAttachmentOption?: boolean;
  hideContextButton?: boolean;
  messageModel?: AiModel | AiModelWithoutAuto;
  noTopPadding?: boolean;
}>();

const emit = defineEmits<{
  updateExtras: [extras: ExtraItem[]];
  openAttachmentInput: [];
  insertSkill: [skill: Skill];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const userStore = useUserStore();

const menu = ref<ComponentExposed<typeof MultiselectDropdownMenu> | null>(null);
const attachmentChips = ref<InstanceType<typeof AttachmentChips> | null>(null);
const draft = computed(() => dataStore.getDraftMessageByChatDuid(props.chat.duid));
const connectorsSettingsLink = makeLinkToSettingsRef("connectors");
const enabledConnectorCount = computed(() => dataStore.connectorList.filter((connector) => connector.enabled).length);
const enabledConnectorCountText = computed(() =>
  getItemCountText(enabledConnectorCount.value, "connector", { capitalize: true })
);
const targetAgent = computed(() =>
  props.chat.targetUserDuid ? dataStore.getUserByDuid(props.chat.targetUserDuid) : null
);
const isLocalAgentChat = computed(() => targetAgent.value?.agent?.executionMode === ExecutionMode.LOCAL);
const showWebAccess = computed(() => !isLocalAgentChat.value);
const showConnectorsLink = computed(
  () =>
    !isLocalAgentChat.value && (userStore.isRoleGte(UserRole.MEMBER) || userStore.roleNorm === UserRole.TECHNICAL_ADMIN)
);

const displayedExtras = computed(() => props.extras.filter((e) => e.duid !== props.chat.rootTaskDuid));
const existingEntitiesDuidsSet = computed(
  () => new Set([...props.extras.map((e) => e.duid), props.chat.rootTaskDuid].filter(isDefined))
);
const pageLinkMode = computed(() => (props.isDraft ? PageLinkMode.CHAT_ENABLED : PageLinkMode.CHAT_DISABLED));

const updateDraftExtras = async (newExtras: ExtraItem[]) => {
  if (!props.isDraft) {
    return;
  }

  const seenExtras = new Set();
  const uniqueExtras = newExtras.filter((e) => {
    const key = e.kind === ENTITY_KIND_URL ? `${e.url}` : `${e.duid}`;
    if (seenExtras.has(key)) {
      return false;
    }
    seenExtras.add(key);
    return true;
  });

  if (!draft.value) {
    emit("updateExtras", uniqueExtras);
    return;
  }

  await dataStore.updateChatDraftMessage(props.chat.duid, { ...draft.value, extras: uniqueExtras });
};

const onSelectEntity = async (option: TypeaheadOption) => {
  if (option.value === ADD_ATTACHMENT_OPTION_VALUE) {
    emit("openAttachmentInput");
    menu.value?.close();
    return;
  }
  if (typeof option.value !== "string" || !option.specialEntity) {
    return;
  }
  if (option.specialEntity === TypeaheadSpecialEntity.SKILL) {
    const skill = dataStore.getSkillByDuid(option.value);
    if (!skill) {
      return;
    }
    emit("insertSkill", skill);
    menu.value?.close();
    return;
  }
  const kind = TYPEAHEAD_ENTITY_TO_ENTITY_NAME_MAP.get(option.specialEntity);
  if (!kind) {
    return;
  }
  updateDraftExtras([...(draft.value?.extras ?? props.extras), { kind, duid: option.value }]);
};

const removeEntity = (entity: ExtraItem) => {
  const extras = draft.value?.extras ?? props.extras;
  updateDraftExtras(extras.filter((e) => e.duid !== entity.duid || e.url !== entity.url));
};

const search = ref("");
const scrollContainer = computed(() => menu.value?.scrollContainer);
const allOptions = ref<TypeaheadGetOptionsResult[]>([]);

const getOptionsFns = computed(() =>
  createChatContextOptionFns(appStore.currentPage, {
    includeSkills: true,
    hideAttachmentOption: props.hideAttachmentOption,
  })
);

const loadMoreOptions = async (item: TypeaheadGetOptionsResult) => {
  if (!item.loadMore || item.options.length >= item.total) {
    return item;
  }
  const response = await item.loadMore(item.options.length);
  return { ...item, options: Array.from(new Set([...item.options, ...response.options])), total: response.total };
};

const { isLoading, reset } = usePaginatedList(
  computed(() => `chat-context-${search.value}`),
  scrollContainer,
  computed(() => allOptions.value.reduce((acc, e) => acc + e.options.length, 0)),
  async () => {
    if (allOptions.value.length === 0) {
      const results = await Promise.allSettled(getOptionsFns.value.map((getOptions) => getOptions(search.value)));
      allOptions.value = results
        .map((result) => (result.status === "fulfilled" ? result.value : null))
        .filter(isDefined);
    } else {
      allOptions.value = await Promise.all(allOptions.value.map(loadMoreOptions));
    }
    return allOptions.value.reduce((acc, e) => acc + e.total, 0);
  }
);

const formatTypeaheadOptions = (options: TypeaheadOption[], keyPrefix?: string): MultiselectDropdownMenuItem[] =>
  options
    .filter((e) => typeof e.value === "string" && !existingEntitiesDuidsSet.value.has(e.value))
    .map((option): MultiselectDropdownMenuItem => {
      const shouldUseTooltip =
        option.specialEntity === TypeaheadSpecialEntity.TASK ||
        option.specialEntity === TypeaheadSpecialEntity.DOC ||
        option.specialEntity === TypeaheadSpecialEntity.SKILL;

      return {
        value: option.value,
        label: option.label,
        adtlSearchTerms: option.adtlSearchTerms,
        selected: false,
        customKey: `item-${keyPrefix}-${option.value}`,
        component: markRaw(shouldUseTooltip ? TypeaheadEntityItem : DropdownMenuItemContent),
        componentArgs: shouldUseTooltip
          ? {
              item: option,
            }
          : {
              title: option.label,
              icon: option.icon ? markRaw(option.icon) : option.icon,
              iconArgs: option.iconArgs,
              item: option,
            },
      };
    });

const contextSections = computed(() =>
  allOptions.value
    .map(({ options, key, title }, index) => ({
      title: title ?? key,
      showTitle: true,
      items: formatTypeaheadOptions(options, `section-${index}`),
    }))
    .filter(({ items }) => items.length > 0)
);

const onAddEntity = async (value: string | boolean | number | null) => {
  const selectedItem = contextSections.value.flatMap((section) => section.items).find((item) => item.value === value);
  const option = (selectedItem?.componentArgs as { item?: TypeaheadOption } | undefined)?.item;
  if (option) {
    await onSelectEntity(option);
  }
};

const onSearch = (value: string) => {
  search.value = value;
  allOptions.value = [];
  reset();
};

watch(
  () => menu.value?.isOpen,
  (isMenuOpen) => isMenuOpen && onSearch(search.value)
);

const addUrl = (value: string) => {
  updateDraftExtras([...(draft.value?.extras ?? props.extras), { kind: ENTITY_KIND_URL, url: value }]);
};

const validate = (value: string): ValidationFunctionResult =>
  isValidUrlIncludingLocal(value) ? { isValid: true } : { isValid: false, error: "Invalid URL" };

const openUrl = (url: string | undefined) => {
  if (!url) {
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

const visibleEntities = computed(() => {
  if (displayedExtras.value.length > MAX_VISIBLE_ENTITIES) {
    return displayedExtras.value.slice(0, MAX_VISIBLE_ENTITIES - 1);
  }
  return displayedExtras.value;
});
const hiddenEntities = computed(() => {
  if (displayedExtras.value.length > MAX_VISIBLE_ENTITIES) {
    return displayedExtras.value.slice(MAX_VISIBLE_ENTITIES - 1);
  }
  return [];
});
const hiddenEntitiesCount = computed(() => hiddenEntities.value.length);
const hiddenEntitiesSections = computed(() => [
  {
    title: "Hidden extras",
    items: hiddenEntities.value
      .map((entity) => {
        if (entity.kind === ENTITY_KIND_URL) {
          return entity.url;
        }
        if (!entity.duid) {
          return undefined;
        }
        const entityData = dataStore.getEntity(entity.duid, entity.kind);
        if (!entityData) {
          return undefined;
        }
        return getTitleOrNoTitle(entityData.title);
      })
      .filter(isDefined),
  },
]);

const toggleWebEnabled = (value: boolean) => dataStore.updateChat({ duid: props.chat.duid, webEnabled: value });

const toggleIncludeUiContext = (value: boolean) =>
  dataStore.updateChat({ duid: props.chat.duid, extrasAreUserManaged: !value });

const addAttachments = (files: File[]) => {
  attachmentChips.value?.addAttachments(files);
};

const isAttachmentUploading = computed(() => attachmentChips.value?.isAttachmentUploading ?? false);

defineExpose({
  addAttachments,
  removeIncompatibleAttachments: () => attachmentChips.value?.removeIncompatibleAttachments(),
  isAttachmentUploading,
});
</script>

<template>
  <div
    v-if="!isMenu"
    class="flow-root"
    :class="
      (displayedExtras.length > 0 || attachmentDuids.length > 0) && [
        'mb-0.5 px-1 text-vlt',
        !isDraft ? '-mx-1 mt-1.5' : !noTopPadding && 'pt-1',
      ]
    ">
    <div
      class="flex min-w-0 flex-wrap items-center gap-1"
      :class="[(displayedExtras.length > 0 || attachmentDuids.length > 0) && 'mb-0.5', isDraft && 'mx-1']">
      <template v-for="entity in visibleEntities" :key="entity.url ?? entity.duid">
        <PageLink
          v-if="entity.kind === ENTITY_KIND_URL"
          :title="entity.url ?? ''"
          :mode="pageLinkMode"
          :icon="LinkIcon"
          :icon-args="{ class: 'icon-xs' }"
          not-in-text
          class="!inline-flex min-w-0 align-middle text-xs"
          @click="openUrl(entity.url)"
          @remove="() => removeEntity(entity)" />

        <DartLink
          v-else
          :mode="pageLinkMode"
          :name="entity.kind"
          :duid="entity.duid"
          not-in-text
          shrink
          class="min-w-0 text-xs"
          @remove="() => removeEntity(entity)" />
      </template>

      <AttachmentChips
        ref="attachmentChips"
        :chat-duid="chat.duid"
        :message-model="messageModel"
        :attachment-duids="attachmentDuids"
        :mode="pageLinkMode" />

      <Tooltip v-if="hiddenEntitiesCount > 0" :sections="hiddenEntitiesSections">
        <div class="inline-flex h-5 cursor-default items-center gap-1.5 rounded-sm bg-lt px-1 text-xs">
          <span class="text-lt">+{{ hiddenEntitiesCount }}</span>
        </div>
      </Tooltip>
    </div>
  </div>

  <MultiselectDropdownMenu
    v-else-if="isDraft && !hideContextButton"
    ref="menu"
    :sections="contextSections"
    placeholder="Add context..."
    header-title="Add context"
    cover
    :placement="Placement.TOP_LEFT"
    :distance="0"
    infinite-list
    :loading-more="isLoading"
    :make-new-entry-text="(text: string) => `Add link ${text}`"
    :popper-class="LEXICAL_OUTSIDE_CLASS"
    :validate="validate"
    enable-new-entry
    @search="onSearch"
    @add="onAddEntity"
    @create="addUrl">
    <template #footer>
      <div
        class="w-full border-t border-md md:rounded-b md:border-x md:border-t-0 md:border-b md:bg-lt md:py-1 md:app-drag-none">
        <DropdownToggleItem
          :icon="ViewIcon"
          title="Include UI context"
          :value="!chat.extrasAreUserManaged"
          :on-update="toggleIncludeUiContext" />
        <DropdownToggleItem
          v-if="showWebAccess"
          :icon="WebsiteIcon"
          title="Web access"
          :value="chat.webEnabled"
          :on-update="toggleWebEnabled" />
        <RouterLink
          v-if="showConnectorsLink"
          :to="connectorsSettingsLink"
          class="group flex w-full text-md focus-ring-none hover:bg-md focus:bg-md"
          @click="menu?.close()">
          <DropdownMenuItemContent
            :icon="ConnectorsIcon"
            :icon-args="{ class: 'icon-sm text-lt' }"
            :icon-after="OpenInternalLinkIcon"
            :icon-after-args="{ class: 'hidden text-lt icon-sm group-hover:block group-focus:block' }"
            :title="enabledConnectorCountText" />
        </RouterLink>
      </div>
    </template>
    <Tooltip text="Add context" class="ml-1.5">
      <Button
        :btn-style="ButtonStyle.SECONDARY"
        :size="ButtonSize.CHIP"
        :icon="PlusIcon"
        :icon-size="IconSize.M"
        :icon-args="{ class: 'text-vlt' }"
        borderless
        is-contrast
        a11y-label="Add context"
        class="!p-0.5" />
    </Tooltip>
  </MultiselectDropdownMenu>
</template>
